import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCustomerDrawer } from "@/lib/useCustomerDrawer";
import CustomerSnapshotTrigger from "@/components/CustomerSnapshot";

/**
 * Renders a customer name as a lightweight, keyboard-accessible link that
 * opens the Customer 360 drawer. Uses only DSG-001 semantic tokens.
 *
 * Two resolution paths:
 *   • `customerId` — direct open (preferred, used by order/customer tables).
 *   • `accountNumber` — resolved via GET /api/customers/by-account/:acct at
 *     click time, then opened. Used by task rows and fleet-map popups where
 *     the ERP account number is the only stable identifier we hold.
 *
 * Snapshot popover: a small "i" icon opens the V1.1.1 Customer Snapshot
 * inline without leaving the current screen. Set `withSnapshot={false}`
 * to hide the icon (e.g. inside a compact table where the icon adds
 * noise).
 */
export default function CustomerLink({
  customerId,
  accountNumber,
  customerName,
  className = "",
  testId,
  children,
  withSnapshot = true,
}) {
  const { openCustomer } = useCustomerDrawer();
  const [resolving, setResolving] = useState(false);
  const label = children ?? customerName;

  if (!customerId && !accountNumber) {
    return <span className={className} data-testid={testId}>{label}</span>;
  }

  const onClick = async (e) => {
    e.stopPropagation();
    if (customerId) {
      openCustomer(customerId);
      return;
    }
    // accountNumber path — one API round-trip to resolve then open.
    setResolving(true);
    try {
      const r = await api.get(`/customers/by-account/${encodeURIComponent(accountNumber)}`);
      if (r?.data?.id) openCustomer(r.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Customer ${accountNumber} not found`);
    } finally {
      setResolving(false);
    }
  };

  const linkButton = (
    <button
      type="button"
      data-testid={testId || `customer-link-${customerId || accountNumber}`}
      onClick={onClick}
      disabled={resolving}
      className={
        "inline-flex items-center rounded-sm text-left font-medium text-ink " +
        "hover:text-brand hover:underline focus:outline-none " +
        "focus-visible:shadow-token-focus disabled:opacity-60 " +
        className
      }
    >
      {label}
    </button>
  );

  if (!withSnapshot) return linkButton;
  return (
    <CustomerSnapshotTrigger customerId={customerId} accountNumber={accountNumber}>
      {linkButton}
    </CustomerSnapshotTrigger>
  );
}
